<script setup>
import { computed } from 'vue';
import { breakOptions, isValidBreak } from './breakTimeHelper';

// A pause (e.g. lunch) inside one day's open window, edited under that day's
// BusinessDay row. The value is `{ start, end }` in minutes from midnight, or
// null for no pause — the shape the schedule's zimobi_break_* columns store.
const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  // The day's open window, in minutes from midnight.
  openWindow: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const DEFAULT_BREAK = { start: 12 * 60, end: 13 * 60 };

const pad = value => String(value).padStart(2, '0');
const label = minute => `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;

const options = computed(() => breakOptions(props.openWindow));

const hasError = computed(
  () => !isValidBreak(props.modelValue, props.openWindow)
);

const isEnabled = computed({
  get: () => Boolean(props.modelValue),
  set: value => {
    if (!value) {
      emit('update:modelValue', null);
      return;
    }
    const [first, second] = options.value;
    emit(
      'update:modelValue',
      isValidBreak(DEFAULT_BREAK, props.openWindow)
        ? DEFAULT_BREAK
        : { start: first, end: second }
    );
  },
});

const update = (part, value) => {
  emit('update:modelValue', { ...props.modelValue, [part]: Number(value) });
};
</script>

<template>
  <div
    v-if="options.length >= 2 || modelValue"
    class="flex items-center gap-2 py-2 ltr:pl-6 rtl:pr-6 text-sm border-b border-solid border-n-weak"
  >
    <input
      v-model="isEnabled"
      type="checkbox"
      class="m-0"
      data-testid="break-enabled"
    />
    <span class="font-medium min-w-28">
      {{ $t('CARDS.FORMS.BREAK.LABEL') }}
    </span>
    <template v-if="modelValue">
      <select
        :value="modelValue.start"
        data-testid="break-start"
        class="w-28 mb-0"
        @change="event => update('start', event.target.value)"
      >
        <option v-for="minute in options" :key="minute" :value="minute">
          {{ label(minute) }}
        </option>
      </select>
      <span>—</span>
      <select
        :value="modelValue.end"
        data-testid="break-end"
        class="w-28 mb-0"
        @change="event => update('end', event.target.value)"
      >
        <option v-for="minute in options" :key="minute" :value="minute">
          {{ label(minute) }}
        </option>
      </select>
      <span
        v-if="hasError"
        data-testid="break-error"
        class="text-xs text-n-ruby-9"
      >
        {{ $t('CARDS.FORMS.BREAK.INVALID') }}
      </span>
    </template>
  </div>
</template>
